/**
 * Soft-warn / hard-require National Course Directory course when an event is commission-reportable.
 * Legacy: events with only course_catalog_version_id still count as linked (Follow-up B migration).
 */

import {
  isTicsEventCourseRequiredForReportable,
  TICS_EVENT_KINDS,
  type TicsEventKind,
} from './ticsFlags.js';

export type TicsEventCourseLinkCheckInput = {
  willBeReportedToCommission?: boolean | null;
  courseCatalogId?: number | string | bigint | null;
  /** Legacy / compat snapshot — counts as linked when course_catalog_id is still null. */
  courseCatalogVersionId?: number | string | bigint | null;
  /**
   * Status of the linked `course_catalog` row. Hosts can link an unpublished draft
   * they created inline, which is *not* a registry course yet. Omit when unknown —
   * the check then falls back to id presence (legacy call sites).
   */
  courseCatalogStatus?: string | null;
};

export type TicsEventCourseLinkCheckResult =
  | { ok: true; softWarn: boolean }
  | {
      ok: false;
      softWarn: true;
      errorCode: 'TICS_COURSE_REQUIRED_FOR_REPORTABLE' | 'TICS_COURSE_DRAFT_NOT_REPORTABLE';
      message: string;
    };

function hasPositiveId(value: number | string | bigint | null | undefined): boolean {
  return value != null && String(value).trim() !== '' && Number(value) > 0;
}

export function eventHasDirectoryCourseLink(input: {
  courseCatalogId?: number | string | bigint | null;
  courseCatalogVersionId?: number | string | bigint | null;
}): boolean {
  return hasPositiveId(input.courseCatalogId) || hasPositiveId(input.courseCatalogVersionId);
}

/**
 * A course link only satisfies the reportable requirement when the course is actually
 * in the public registry. An unpublished draft has no TCI, so a commission has nothing
 * to reconcile the roster against.
 */
export function isReportableCourseStatus(status?: string | null): boolean {
  if (status == null || status === '') return true; // unknown → legacy id-presence behavior
  return status === 'published';
}

export function checkTicsEventCourseLink(
  input: TicsEventCourseLinkCheckInput,
  env: NodeJS.ProcessEnv = process.env,
): TicsEventCourseLinkCheckResult {
  const reportable = Boolean(input.willBeReportedToCommission);
  const linked = eventHasDirectoryCourseLink(input);

  if (reportable && linked && !isReportableCourseStatus(input.courseCatalogStatus)) {
    return {
      ok: false,
      softWarn: true,
      errorCode: 'TICS_COURSE_DRAFT_NOT_REPORTABLE',
      message:
        'This course is still a draft awaiting directory review. Reportable events need a published National Course Directory course.',
    };
  }

  if (!reportable || linked) {
    return { ok: true, softWarn: false };
  }

  if (isTicsEventCourseRequiredForReportable(env)) {
    return {
      ok: false,
      softWarn: true,
      errorCode: 'TICS_COURSE_REQUIRED_FOR_REPORTABLE',
      message:
        'A National Course Directory course is required when this event will be reported to a state commission.',
    };
  }

  return { ok: true, softWarn: true };
}

/** @deprecated Use checkTicsEventCourseLink — kept for older call sites. */
export type TicsEventCourseVersionCheckInput = TicsEventCourseLinkCheckInput & {
  courseCatalogVersionId?: number | string | bigint | null;
};

/** @deprecated Use checkTicsEventCourseLink */
export type TicsEventCourseVersionCheckResult = TicsEventCourseLinkCheckResult;

/** @deprecated Use checkTicsEventCourseLink */
export function checkTicsEventCourseVersion(
  input: TicsEventCourseVersionCheckInput,
  env: NodeJS.ProcessEnv = process.env,
): TicsEventCourseVersionCheckResult {
  return checkTicsEventCourseLink(input, env);
}

export function isValidTicsEventKind(value: unknown): value is TicsEventKind {
  return typeof value === 'string' && (TICS_EVENT_KINDS as readonly string[]).includes(value);
}
